import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

/** One role + its org nodes on a user (as posted to the API). */
export interface UserRoleAssignment {
  role: number;
  org_level: number[];
}

/**
 * A user row as returned by the backend. Field names vary, so they're read via the
 * accessors below. The user-list design (Figma 632:4824) drives which fields the API
 * should provide: name, emp_id, email, contact, roles[], org hierarchy, status,
 * last-seen, and an optional avatar.
 */
export interface AppUser {
  id: number;
  fullName?: string | null;
  full_name?: string | null;
  name?: string | null;
  email?: string | null;
  contact?: string | null;
  phone?: string | null;
  phone_number?: string | null;
  emp_id?: string | null;
  created_at?: string | null;
  org_hierarchy?: string | null;
  hierarchy?: string | null;
  last_seen?: string | null;
  avatar?: string | null;
  photo?: string | null;
  image?: string | null;
  is_active?: boolean;
  active?: boolean;
  organization?: number | null;
  roles?: Array<
    | string
    | {
        role?: number | { id: number; name?: string };
        role_name?: string;
        name?: string;
        org_level?: number | number[];
        org_level_name?: string;
        org_level_names?: string[];
      }
  >;
}

/** Result of a bulk user import (shape is best-effort; read defensively). */
export interface UserImportResult {
  created?: number;
  updated?: number;
  failed?: number;
  errors?: Array<string | { row?: number; message?: string }>;
  detail?: string;
}

/** An org node option for the create-user dropdowns. */
export interface OrgNodeOption {
  id: number;
  org_name?: string;
  name?: string;
  level_name?: string;
  level_type?: number;
}

/** Request body for POST /access/users/. */
export interface CreateUserPayload {
  fullName: string;
  email: string;
  contact: string;
  organization: number;
  roles: UserRoleAssignment[];
  active: boolean;
}

/** DRF list responses may be a plain array or a paginated `{ results }` envelope. */
type Listish<T> = T[] | { count?: number; results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

/** Normalized list result the users table consumes. */
export interface UsersResponse {
  items: AppUser[];
  total: number;
  page: number;
  pageSize: number;
}

/* ------------------------------------------------------------------ *
 * Defensive accessors — the API's exact field names aren't guaranteed.
 * ------------------------------------------------------------------ */
export const userName = (u: AppUser) => u.fullName || u.full_name || u.name || '—';
export const userEmail = (u: AppUser) => u.email || '';
export const userPhone = (u: AppUser) => u.contact || u.phone || u.phone_number || '';
export const userActive = (u: AppUser) => u.is_active ?? u.active ?? true;
export const userEmpId = (u: AppUser) => u.emp_id || '';
export const userLastSeen = (u: AppUser) => u.last_seen || '';
export const userAvatar = (u: AppUser) => u.avatar || u.photo || u.image || '';
export const userRoleNames = (u: AppUser): string[] =>
  (u.roles ?? [])
    .map((r) => {
      if (typeof r === 'string') return r;
      if (typeof r?.role === 'object' && r.role) return r.role.name ?? '';
      return r?.role_name ?? r?.name ?? '';
    })
    .filter(Boolean);
/** The org node / hierarchy the user belongs to (top-level field or first role's org). */
export const userHierarchy = (u: AppUser): string => {
  if (u.org_hierarchy) return u.org_hierarchy;
  if (u.hierarchy) return u.hierarchy;
  const withOrg = (u.roles ?? []).find((r) => typeof r === 'object' && r?.org_level_name);
  return (typeof withOrg === 'object' && withOrg?.org_level_name) || '—';
};

export const usersApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getUsers: build.query<
      UsersResponse,
      | {
          search?: string;
          isActive?: boolean | '';
          role?: number | '';
          organization?: number | '';
          orgLevel?: number | string | '';
          page?: number;
          pageSize?: number;
          /** Fetch the whole list without pagination (e.g. for a dropdown). */
          all?: boolean;
        }
      | void
    >({
      query: (arg) => ({
        url: `${ApiEndPoints.users}/`,
        params: {
          ...(arg?.search ? { search: arg.search } : {}),
          ...(arg?.isActive === true || arg?.isActive === false ? { is_active: arg.isActive } : {}),
          ...(arg?.role ? { role: arg.role } : {}),
          ...(arg?.organization ? { organization: arg.organization } : {}),
          ...(arg?.orgLevel ? { org_level: arg.orgLevel } : {}),
          // `all` skips pagination entirely; otherwise send page/page_size.
          ...(arg?.all ? {} : { page: arg?.page ?? 1, page_size: arg?.pageSize ?? 10 }),
        },
      }),
      transformResponse: (r: Listish<AppUser>, _meta, arg) => {
        const items = asList(r);
        const total = Array.isArray(r) ? r.length : (r?.count ?? items.length);
        return { items, total, page: arg?.page ?? 1, pageSize: arg?.pageSize ?? 10 };
      },
      providesTags: ['Users'],
    }),
    getUser: build.query<AppUser, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.users}/${id}/` }),
      providesTags: ['Users'],
    }),
    createUser: build.mutation<AppUser, CreateUserPayload>({
      query: (body) => ({
        url: `${ApiEndPoints.users}/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Users'],
    }),
    updateUser: build.mutation<AppUser, { id: number | string; patch: Record<string, unknown> }>({
      query: ({ id, patch }) => ({
        url: `${ApiEndPoints.users}/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Users'],
    }),
    deleteUser: build.mutation<void, number | string>({
      query: (id) => ({
        url: `${ApiEndPoints.users}/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),
    // Download the server-generated bulk-upload template (.xlsx) for an organization.
    getUserImportTemplate: build.query<Blob, { organization: number | string }>({
      query: ({ organization }) => ({
        url: `${ApiEndPoints.usersImportTemplate}/`,
        params: { organization },
        responseHandler: (response) => response.blob(),
        cache: 'no-cache',
      }),
    }),
    // Bulk-upload a filled template. Body is multipart/form-data (fetchBaseQuery
    // sets the boundary/Content-Type automatically for a FormData body).
    importUsers: build.mutation<UserImportResult, { file: File }>({
      query: ({ file }) => {
        const form = new FormData();
        form.append('file', file);
        return { url: `${ApiEndPoints.usersImport}/`, method: 'POST', body: form };
      },
      invalidatesTags: ['Users'],
    }),
    // Org nodes for the create-user org dropdown (optionally filtered to one level type).
    getOrgNodes: build.query<
      OrgNodeOption[],
      { organization: number | string; levelType?: number }
    >({
      query: ({ organization, levelType }) => ({
        url: `${ApiEndPoints.organizationNode}/`,
        params: {
          organization,
          ...(levelType != null ? { level_type: levelType } : {}),
        },
      }),
      transformResponse: (r: Listish<OrgNodeOption>) => asList(r),
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetOrgNodesQuery,
  useLazyGetUserImportTemplateQuery,
  useImportUsersMutation,
} = usersApi;
